import type { ComponentType } from 'react';

import BpkMobileScrollContainer from '@skyscanner-internal/backpack-web/bpk-component-mobile-scroll-container';
import BpkPanel from '@skyscanner-internal/backpack-web/bpk-component-panel';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import classNames from 'classnames';

import withErrorBoundary from '../ErrorBoundary';
import { iconsMap } from '../InfoSnippetDecoupled/Snippet';
import { useMBDContext } from '../common/MBD/useMBDContext';

import type {
  ComparisonCardsCard,
  ComparisonCardsItem,
  ComparisonCardsProps,
} from '@skyscanner-internal/landing-pages-shared-types/types-shared/ComparisonCardsProps';

import STYLES from './ComparisonCards.module.scss';

const getIcon = (icon: ComparisonCardsItem['icon']) =>
  iconsMap[icon] as ComponentType<{ className?: string }> | undefined;

const ComparisonItem = ({ item }: { item: ComparisonCardsItem }) => {
  const Icon = getIcon(item.icon);

  return (
    <li className={STYLES.ComparisonItem}>
      <span className={STYLES.ComparisonItem__icon} aria-hidden="true">
        {Icon && <Icon />}
      </span>
      <BpkText textStyle={TEXT_STYLES.bodyDefault}>{item.text}</BpkText>
    </li>
  );
};

const ComparisonCard = ({ card }: { card: ComparisonCardsCard }) => (
  <div className={STYLES.ComparisonCardWrapper}>
    <BpkPanel
      className={classNames(STYLES.ComparisonCard, {
        [STYLES.ComparisonCard_highlighted]: card.highlighted,
      })}
      padded={false}
    >
      <div className={STYLES.ComparisonCard__content}>
        <div className={STYLES.ComparisonCard__header}>
          <BpkText
            textStyle={TEXT_STYLES.heading3}
            color={TEXT_COLORS.textPrimary}
            tagName="h3"
          >
            {card.title}
          </BpkText>
          <BpkText textStyle={TEXT_STYLES.bodyDefault}>{card.subtitle}</BpkText>
        </div>
        <ul className={STYLES.ComparisonCard__items}>
          {card.items.map((item) => (
            <ComparisonItem key={`${item.icon}-${item.text}`} item={item} />
          ))}
        </ul>
      </div>
    </BpkPanel>
  </div>
);

const ComparisonCards = ({ cards, subtitle, title }: ComparisonCardsProps) => {
  const { buildMBDParam, rawComponentName } = useMBDContext();

  if (!cards?.length) {
    return null;
  }

  const renderCards = () =>
    cards.map((card) => (
      <ComparisonCard key={`${card.title}-${card.subtitle}`} card={card} />
    ));

  return (
    <div
      className={STYLES.ComparisonCards}
      data-testid="comparisonCards"
      data-tracking-element-id={rawComponentName}
      data-tracking-common-params={buildMBDParam(rawComponentName)}
    >
      <div className={STYLES.ComparisonCards__header}>
        <BpkText
          textStyle={TEXT_STYLES.heading2}
          color={TEXT_COLORS.textPrimary}
          tagName="h2"
        >
          {title}
        </BpkText>
        {subtitle && (
          <BpkText textStyle={TEXT_STYLES.bodyDefault}>{subtitle}</BpkText>
        )}
      </div>
      <div className={STYLES.ComparisonCards__mobileScroller}>
        <BpkMobileScrollContainer>
          <div className={STYLES.ComparisonCards__mobileCards}>
            {renderCards()}
          </div>
        </BpkMobileScrollContainer>
      </div>
      <div className={STYLES.ComparisonCards__desktopCards}>
        {renderCards()}
      </div>
    </div>
  );
};

const ComparisonCardsWithErrorBoundary = withErrorBoundary(
  ComparisonCards,
  'comparison-cards-component',
);

export default ComparisonCardsWithErrorBoundary;
